/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { DEFAULT_GROUP_COLOR } from '@g17eco/molecules/form';
import { AccessType, MetricGroup, MetricGroupDropdownItem, MetricGroupSourceType } from '../types/metricGroup';
import { naturalSort } from '.';
import { UniversalTrackerBlueprintMin, UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { getName, getTypeCode, hasFramework, hasStandard } from './universalTracker';
import { getAltCode } from './alternativeCode';
import { CustomMetricOrderType, ScopeGroupData, ScopeQuestion, ScopeQuestionOptionalValue } from '@g17eco/types/survey';
import { PACK, QUESTION } from '@constants/terminology';

export const getGroupLabel = (group: MetricGroup) => {
  if (group.accessType === AccessType.Inherited) {
    return `Inherited: ${group.groupName}`;
  }

  if (group.accessType === AccessType.Assigned && group.initiative) {
    return `${group.initiative.name} - ${group.groupName}`;
  }

  return group.groupName;
};

export const getCustomGroupOptions = (metricGroups: MetricGroupDropdownItem[]) =>
  metricGroups
    .map((group) => {
      return {
        label: (
          <div className='d-flex align-items-center'>
            {group.groupData?.icon ? (
              <img src={group.groupData.icon} alt={group.groupName} width={26} className='mr-2' />
            ) : (
              <i
                className='fa fa-circle mr-2'
                style={{ color: group.groupData?.colour ?? DEFAULT_GROUP_COLOR, fontSize: '26px' }}
              ></i>
            )}
            <span>CM: {group.groupName}</span>
          </div>
        ),
        searchString: group.groupName,
        value: group._id,
        scopeType: 'custom' as const,
      };
    })
    .sort((a, b) => naturalSort(a.searchString, b.searchString));

const objectIdLength = 24;

export const filterMetricGroups = ({
  utrId,
  metricGroupId,
  metricGroups = [],
}: {
  utrId: string;
  metricGroupId: string;
  metricGroups?: Pick<MetricGroup, '_id' | 'universalTrackers'>[];
}) => {
  if (metricGroupId.length !== objectIdLength) {
    return false;
  }
  const metricGroup = metricGroups.find((g) => g._id === metricGroupId);
  return metricGroup?.universalTrackers?.includes(utrId);
};

export const isMatchSelectedPacks = ({
  utr,
  packs,
  metricGroups,
}: {
  utr: UniversalTrackerBlueprintMin;
  packs: string[];
  metricGroups: MetricGroupDropdownItem[];
}) => {
  if (packs.length === 0) {
    return true;
  }
  for (const pack of packs) {
    if (hasStandard(utr, pack) || hasFramework(utr, pack)) {
      return true;
    }
    if (filterMetricGroups({ utrId: utr._id, metricGroupId: pack, metricGroups })) {
      return true;
    }
  }
  return false;
};

export function sortCustomGroupQuestions<
  T extends Pick<ScopeQuestionOptionalValue, 'name' | 'universalTracker'> = ScopeQuestion
>(
  questions: T[] | undefined,
  group: Pick<ScopeGroupData, 'preferredAltCodes' | 'metricsOrder' | 'universalTrackerIds'>
): T[] {
  const { preferredAltCodes, metricsOrder, universalTrackerIds } = group;
  if (!questions?.length) {
    return [];
  }

  // Sort by what the question list shows
  const getCode = (q: T) => getAltCode(q.universalTracker, undefined, preferredAltCodes);

  if (!metricsOrder || metricsOrder.orderType === CustomMetricOrderType.Name) {
    return questions.sort((a, b) =>
      naturalSort(a.universalTracker.getName(getCode(a)), b.universalTracker.getName(getCode(b))),
    );
  }

  const { orderType } = metricsOrder;
  if (orderType === CustomMetricOrderType.Custom) {
    if (!universalTrackerIds?.length) {
      return questions;
    }
    // universalTrackerIds is the latest list of utrIds of metric group
    // questions are not synced with universalTrackerIds when users update the metric group scope
    // Use universalTrackerIds to sort the current questions
    const sortedQuestions = questions.slice().sort((a, b) => {
      const indexA = universalTrackerIds.indexOf(a.universalTracker.getId());
      const indexB = universalTrackerIds.indexOf(b.universalTracker.getId());
      // If an item is not in utrIds, assign it a high value to push it to the end
      return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
    });

    return sortedQuestions;
  }

  // Must be sort by typeCode
  return questions.sort((a, b) =>
    naturalSort(a.universalTracker.getTypeCode(getCode(a)), b.universalTracker.getTypeCode(getCode(b))),
  );
}

export function sortCustomGroupQuestionPlains<T extends UniversalTrackerPlain>(
  questions: T[] | undefined,
  group: Pick<ScopeGroupData, 'preferredAltCodes' | 'metricsOrder' | 'universalTrackerIds'>
): T[] {
  const { preferredAltCodes, metricsOrder, universalTrackerIds } = group;
  if (!questions?.length) {
    return [];
  }

  if (!metricsOrder || metricsOrder.orderType === CustomMetricOrderType.Name) {
    if (preferredAltCodes && preferredAltCodes.length > 0) {
      return questions.sort((a, b) => {
        return naturalSort(
          getName(a, preferredAltCodes),
          getName(b, preferredAltCodes)
        );
      });
    }
    // William thinks this is the same, but Kes is scared
    return questions.sort((a, b) => naturalSort(a.name, b.name));
  }

  const { orderType } = metricsOrder;
  if (orderType === CustomMetricOrderType.Custom) {
    if (!universalTrackerIds?.length) {
      return questions;
    }
    // universalTrackerIds is the latest list of utrIds of metric group
    // questions are not synced with universalTrackerIds when users update the metric group scope
    // Use universalTrackerIds to sort the current questions
    const sortedQuestions = questions.slice().sort((a, b) => {
      const indexA = universalTrackerIds.indexOf(a._id);
      const indexB = universalTrackerIds.indexOf(b._id);
      // If an item is not in utrIds, assign it a high value to push it to the end
      return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
    });

    return sortedQuestions;
  }

  // Must be sort by typeCode
  if (preferredAltCodes && preferredAltCodes.length > 0) {
    return questions.sort((a, b) => {
      return naturalSort(
        getTypeCode(a, preferredAltCodes),
        getTypeCode(b, preferredAltCodes)
      );
    });
  }
  return questions.sort((a, b) => naturalSort(a.typeCode, b.typeCode));
}

// rely on MetricGroupSourceType.Survey for now, might introduce new property (subType) later if needed
export const isMTMetricGroup = (metricGroup: Pick<MetricGroup, 'source'>) => {
  return metricGroup.source && metricGroup.source.type === MetricGroupSourceType.Survey;
};

interface FilterableSubgroup {
  showEmpty?: boolean;
  universalTrackers?: string[];
}

interface FilterableParent {
  showEmpty?: boolean;
  source?: MetricGroup['source'];
}

/**
 * Determines if a subgroup should be visible based on showEmpty flag and metrics count.
 *
 * Logic:
 * 1. If subgroup has showEmpty set, use that value
 * 2. Otherwise, inherit from parent's showEmpty
 * 3. If parent doesn't have showEmpty, default to true for MT assessments, false otherwise
 * 4. Show subgroup if: showEmpty is true OR subgroup has metrics (universalTrackers.length > 0)
 */
export const checkIsVisibleSubgroup = (subgroup: FilterableSubgroup, parent: FilterableParent): boolean => {
  return (
    (subgroup.showEmpty ?? parent.showEmpty ?? isMTMetricGroup(parent)) || (subgroup.universalTrackers?.length ?? 0) > 0
  );
};

export const filterVisibleSubgroups = <T extends FilterableSubgroup>(
  parent: FilterableParent & { subgroups?: T[] }
): T[] => {
  return (parent.subgroups ?? []).filter((subgroup) => checkIsVisibleSubgroup(subgroup, parent));
};

enum VariantType {
  MaterialityTracker = 'materiality-tracker',
  CompanyTracker = 'company-tracker',
}

const VARIANT_MAP: {
  [key in VariantType]: {
    deleteTooltip: string | ((groupName: string) => string);
    deleteDisabled?: boolean;
    editTooltip: string;
    editDisabled?: boolean;
    assignedTooltip?: string;
  };
} = {
  [VariantType.MaterialityTracker]: {
    deleteTooltip: `${PACK.CAPITALIZED_SINGULAR} cannot be deleted`,
    editTooltip: `Please open this ${PACK.SINGULAR} on Materiality Tracker to edit`,
    deleteDisabled: true,
    editDisabled: true,
  },
  [VariantType.CompanyTracker]: {
    deleteTooltip: (groupName: string) => `Delete ${groupName} from Custom ${QUESTION.CAPITALIZED_PLURAL}`,
    editTooltip: 'Edit Custom Metrics',
    assignedTooltip: 'View Custom Metrics',
  },
};

export const getVariant = (metricGroup: MetricGroup) => {
  const key = isMTMetricGroup(metricGroup) ? VariantType.MaterialityTracker : VariantType.CompanyTracker;
  return VARIANT_MAP[key];
};
